import { AxiosPromise, AxiosResponse } from 'axios';
import { DateTime } from 'luxon';
import { action, computed, IObservableArray, observable, toJS, makeObservable } from 'mobx';
import { v4 as uuid } from 'uuid';
import { IFetch } from '../classes/Fetch';
import { ICommitmentException } from '../classes/ICommitmentException';
import { ISpecificException } from '../classes/ISpecificExceptions';
import { ReqExcSections } from '../classes/ReqExcSections';
import { SpecificReqExcData } from '../classes/SpecificReqExcData';
import removeLineBreaks from '../utils/removeLineBreaks';
import { AppService } from './AppService';
import FetchingService from './FetchingService';
import {
  deleteExceptionAsync,
  getExceptionsAsync,
  initExceptionsAsync,
  resetExceptionsAsync,
  saveExceptionsAsync,
} from './PolicyExceptionsServiceRequest';
import { RootStore } from './RootStore';
import { PolicyTypeEnum } from '../classes/PolicyTypeEnum';
import { getExceptionsTemplatesAsync } from 'titleReports/services/RestCommitmentsExceptionsService';

export class PolicyScheduleBService {
  public exceptionsData: ICommitmentException[] = [];

  public pageError: string = '';

  public specificExceptions: SpecificReqExcData = new SpecificReqExcData();

  public allSpecificExceptions: ISpecificException[] = [];

  public loadPage = new FetchingService();

  public saveDataFetch = new FetchingService();

  private appData: AppService;

  private savedData: ICommitmentException[] = [];

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      exceptionsData: observable,
      pageError: observable,
      specificExceptions: observable,
      allSpecificExceptions: observable,
      loadPage: observable,
      saveDataFetch: observable,
      initExceptions: action,
      setPageError: action,
      getPageError: computed,
      initSpecificRequirementsExceptions: action,
      setSpecificExceptions: action,
      setAllSpecificExceptions: action,
      getExceptionsList: action,
      addItem: action,
      addCustomItem: action,
      setItems: action,
      deleteItem: action,
      setChangedText: action,
      onOrderChanged: action,
      getSpecificExceptions: action,
      saveRequirementsExceptions: action,
      resetExceptions: action,
      saveData: action,
      getLoadPage: computed,
      getSaveDataFetch: computed,
      setSaveDataFetchDefault: action,
    });

    this.appData = rootStore.appData;
  }

  public initExceptions(orderId: string, policyType: PolicyTypeEnum) {
    this.loadPage.fetching();

    initExceptionsAsync(orderId, policyType)
      .then((response) => {
        getExceptionsAsync(orderId, policyType)
          .then((response) => {
            const data = this.getInitReqExs(response);
            this.setItems(this.getInitReqExs(data));
            this.savedData = this.getInitReqExs(data);

            this.loadPage.fetchedSuccess();
          })
          .catch((error) => {
            this.loadPage.fetchedError(error);
            this.setPageError(error);
          });
      })
      .catch((error) => {
        this.loadPage.fetchedError(error);
        this.setPageError(error);
      });
    this.initSpecificRequirementsExceptions();
  }

  public setPageError(e: any) {
    this.pageError = e;
  }

  public get getPageError(): string {
    return this.pageError;
  }

  public initSpecificRequirementsExceptions() {
    const order = this.appData.getOrderData;
    getExceptionsTemplatesAsync()
      .then((response: AxiosResponse) => {
        const exceptions: ISpecificException[] = response.data as ISpecificException[];
        this.setAllSpecificExceptions(exceptions);

        const specificExcFilter = (exceptions as ISpecificException[])
          .filter((item) => item.appliesTo.includes(order.transaction_type) || item.appliesOnlyInState.length === 0)
          .filter((item) => item.appliesOnlyInState.includes(order.state) || item.appliesOnlyInState.length === 0)
          .filter(
            (item) => item.appliesOnlyInCounties.includes(order.county_fips) || item.appliesOnlyInCounties.length === 0
          );
        this.setSpecificExceptions(specificExcFilter);
        this.setItems(this.getInitReqExs(this.getExceptionsList()));
      })
      .catch((error) => {});
  }

  public setSpecificExceptions(specificExcFilter: ISpecificException[]) {
    const specificReqExs: SpecificReqExcData = new SpecificReqExcData();
    specificExcFilter.forEach((item: ISpecificException) => {
      specificReqExs[item.schedule].push(item);
    });
    (this.specificExceptions.B1 as IObservableArray).replace(specificReqExs.B1);
    (this.specificExceptions.B2 as IObservableArray).replace(specificReqExs.B2);
  }

  public setAllSpecificExceptions(specificExc: ISpecificException[]) {
    this.allSpecificExceptions = specificExc;
  }

  public getExceptionsList(): ICommitmentException[] {
    return this.exceptionsData;
  }

  public addItem = async (
    sectionName: ReqExcSections,
    item: ISpecificException,
    orderId: string,
    policyType: PolicyTypeEnum
  ) => {
    const newReqExsList = this.getExceptionsList().slice();
    const newItem: ICommitmentException = {
      body: removeLineBreaks(item.template),
      created_at: DateTime.local().toUTC().toISO({ includeOffset: false }),
      filled_out: true,
      id: uuid(),
      note: item.note,
      order_id: orderId,
      position: newReqExsList.length,
      schedule: item.schedule,
      template_id: item.id,
      template_version: 1,
    };
    if (newReqExsList.indexOf(newItem) === -1) {
      newReqExsList.push(newItem);
    } else {
      this.setPageError(`This ${sectionName === ReqExcSections.B1 ? 'requirement' : 'exception'} already exists`);
      window.setTimeout(() => {
        this.setPageError('');
      }, 5000);
    }
    this.setItems(this.changePositionNumber(newReqExsList));
    await this.saveRequirementsExceptions(orderId, policyType);
  };

  public addCustomItem = async (
    sectionName: ReqExcSections,
    customText: string,
    orderId: string,
    policyType: PolicyTypeEnum
  ) => {
    const newReqExsList = this.getExceptionsList().slice();
    newReqExsList.push({
      body: customText,
      created_at: DateTime.local().toUTC().toISO({ includeOffset: false }),
      filled_out: true,
      id: uuid(),
      order_id: orderId,
      position: newReqExsList.length,
      schedule: sectionName,
      template_id: uuid(),
      template_version: 1,
    });
    this.setItems(this.changePositionNumber(newReqExsList));
    await this.saveRequirementsExceptions(orderId, policyType);
  };

  public setItems = (items: ICommitmentException[]) => {
    (this.exceptionsData as IObservableArray).replace(items);
  };

  public deleteItem = async (item: ICommitmentException, orderId: string, policyType: PolicyTypeEnum) => {
    const find = this.savedData.find((e: ICommitmentException) => e.id === item.id);
    (this.getExceptionsList() as IObservableArray).remove(item);
    if (find) {
      try {
        this.saveDataFetch.fetching();
        await deleteExceptionAsync(item.id, orderId, policyType);
        this.saveDataFetch.fetchedSuccess();
      } catch (e: any) {
        this.saveDataFetch.fetchedError(e);
        this.setPageError(e);
      }
    }
  };

  public setChangedText = async (
    sectionName: ReqExcSections,
    newText: string,
    item: ICommitmentException,
    orderId: string,
    policyType: PolicyTypeEnum
  ) => {
    const newList = this.getExceptionsList().slice();
    const index = newList.indexOf(item);
    newList[index] = {
      ...newList[index],
      body: removeLineBreaks(newText),
    };
    this.setItems(newList);
    await this.saveRequirementsExceptions(orderId, policyType);
  };

  public onOrderChanged = async (
    sectionName: ReqExcSections,
    newArray: ICommitmentException[],
    orderId: string,
    policyType: PolicyTypeEnum
  ) => {
    this.setItems(this.changePositionNumber(newArray));
    await this.saveRequirementsExceptions(orderId, policyType);
  };

  public getSpecificExceptions(sectionName: ReqExcSections): ISpecificException[] {
    return this.specificExceptions[sectionName];
  }

  public saveRequirementsExceptions = async (orderId: string, policyType: PolicyTypeEnum) => {
    this.saveDataFetch.fetching();
    return this.saveData(orderId, policyType)
      .then((response) => {
        this.savedData = toJS(this.getExceptionsList());
        this.saveDataFetch.fetchedSuccess();
        return Promise.resolve();
      })
      .catch((error: AxiosResponse) => {
        this.saveDataFetch.fetchedError();
        this.setPageError(error);
        return Promise.reject(error);
      });
  };

  public resetExceptions(orderId: string, policyType: PolicyTypeEnum) {
    this.loadPage.fetching();
    resetExceptionsAsync(orderId, policyType)
      .then((responce) => {
        this.initExceptions(orderId, policyType);
      })
      .catch((error) => {
        this.setPageError(error);
        this.loadPage.fetchedError(error);
      });
  }

  public saveData(orderId: string, policyType: PolicyTypeEnum): AxiosPromise {
    return saveExceptionsAsync(orderId, policyType, this.getExceptionsList());
  }

  public get getLoadPage(): IFetch {
    return this.loadPage.getFetch;
  }

  public get getSaveDataFetch(): IFetch {
    return this.saveDataFetch.getFetch;
  }

  public setSaveDataFetchDefault() {
    return this.saveDataFetch.setFetchDefault();
  }

  private changePositionNumber(array: ICommitmentException[]): ICommitmentException[] {
    array.forEach((item, index) => {
      return (array[index].position = index);
    });
    return array;
  }

  private getInitReqExs(items: ICommitmentException[]): ICommitmentException[] {
    const newItems: ICommitmentException[] = [];
    const specExc: ISpecificException[] = this.allSpecificExceptions;
    items.forEach((item: ICommitmentException) => {
      const newItem = specExc.find((s: ISpecificException) => {
        return s.id === item.template_id;
      });
      if (newItem) {
        newItems.push({
          ...item,
          body: removeLineBreaks(item.body),
          note: newItem.note,
        });
      } else {
        newItems.push(item);
      }
    });
    return newItems.sort((a: ICommitmentException, b: ICommitmentException) =>
      a.position > b.position ? 1 : b.position > a.position ? -1 : 0
    );
  }
}
